<template>
  <div class="h-full p-6 overflow-y-auto w-full">
    <form
      class="space-y-6"
      @submit.prevent="submit"
    >
      <div class="space-y-4">
        <h3 class="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          Procurement Method
        </h3>

        <div class="grid grid-cols-2 gap-3">
          <button
            type="button"
            class="btn text-sm py-3"
            :class="[form.form.procurement_method === 'schedule_of_rates' ? 'btn--primary-blue' : 'btn--secondary-blue']"
            @click="form.form.procurement_method = 'schedule_of_rates'"
          >
            Schedule of Rates
          </button>

          <button
            type="button"
            class="btn text-sm py-3"
            :class="[form.form.procurement_method === 'lump_sum' ? 'btn--primary-blue' : 'btn--secondary-blue']"
            @click="form.form.procurement_method = 'lump_sum'"
          >
            Lump Sum
          </button>
        </div>
      </div>

      <!-- Save Button -->
      <div class="pt-4 border-t border-gray-200">
        <div v-if="hasPackageItems">
          <p class="text-sm bg-yellow-100 text-yellow-800 p-4 rounded-xl">
            You cannot change the procurement method until you remove all package items.
          </p>
        </div>

        <button
          v-else-if="canSave"
          type="submit"
          class="btn btn--primary-blue flex items-center space-x-2 mt-2"
          :disabled="updateAction.is(States.LOADING)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

          <span>Save Procurement Strategy</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { falsey } from '@/helpers';
import { t } from '@/i18n';
import Package from '@/models/Package';
import { usePackagesStore } from '@/store/packages';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { computed, watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  selectedPackage: Package;
  canSave: boolean;
}>();

const packagesStore = usePackagesStore();
const packageTradeItemsStore = usePackageTradeItemsStore();
const updateAction = useStoreApiAction(packagesStore.update);

const form = useFormData({
  procurement_method: '',
});

watch(() => props.selectedPackage, (pkg) => {
  if(pkg) form.form.procurement_method = pkg.procurement_method ?? '';
}, { immediate: true });

const packageTradeItems = computed(() =>
  packageTradeItemsStore.models
    .where('package_id', props.selectedPackage.id)
    .with('trade_item')
    .get()
);

const hasPackageItems = computed(() => {
  const itemsWithRates = packageTradeItems.value.some((pti) => !falsey(pti.rate)) &&
    props.selectedPackage.procurement_method === 'schedule_of_rates';

  const itemsWithValues = packageTradeItems.value.some((pti) => falsey(pti.rate)) &&
    props.selectedPackage.procurement_method === 'lump_sum';

  return itemsWithRates || itemsWithValues;
});

function submit() {
  form.resetErrors();

  updateAction
    .request(props.selectedPackage.id, form.only(['procurement_method']))
    .then(() => {
      toast.success(t('Subcontract.PackagesDashboard.PackageBreakdown.message.packageDetailsUpdated'));
    })
    .catch((err) => {
      form.setErrors(err.data);
    });
}
</script>
